import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CampaignPackage } from "./useActiveCampaign";
import { getPurchaseAttributionBundle } from "@/lib/lastTouchAttribution";
import { capture as posthogCapture } from "@/services/posthog";

export function useCampaignPackagePurchase() {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const purchasePackage = async (pkg: CampaignPackage) => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          title: "Please sign in",
          description: "You need to be signed in to purchase a package.",
          variant: "destructive",
        });
        return null;
      }

      // Choose the right checkout function based on package type
      const functionName = pkg.package_type === 'trial_subscription' 
        ? "create-trial-subscription-checkout" 
        : "create-campaign-checkout";

      const attribution = getPurchaseAttributionBundle();
      const { data, error } = await supabase.functions.invoke(
        functionName,
        {
          body: { packageId: pkg.id, ...attribution },
        }
      );

      if (error) {
        console.error("Checkout error:", error);
        toast({
          title: "Error",
          description: error.message || "Failed to create checkout session",
          variant: "destructive",
        });
        return null;
      }

      if (data?.clientSecret) {
        posthogCapture("campaign_package_started", {
          package_id: pkg.id,
          package_type: pkg.package_type,
          package_name: (pkg as any).name ?? undefined,
        });
        // Navigate to embedded checkout page
        const params = new URLSearchParams({
          clientSecret: data.clientSecret,
          returnTo: "/offers",
        });
        navigate(`/checkout?${params.toString()}`);
        return data.clientSecret;
      }

      if (data?.error) {
        toast({
          title: "Not eligible",
          description: data.error,
          variant: "destructive",
        });
        return null;
      }

      return null;
    } catch (error: any) {
      console.error("Purchase error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to process purchase",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { purchasePackage, isLoading };
}
